import React, { useState, useRef, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import {
  AcmeCoOperationErrorIcon,
  BackArrowIcon,
  BluePlusIcon,
  FeedbackAnalyticsIcon,
  FeedbackDashbordIcon,
  FeedbackListIcon,
  NewNotificationIcon,
  plusIcon,
  RoadMapIcon,
} from "../../assets/icons";
import TabsHeader from "../../components/TabsHeader";
import FeedbackDashboard from "./FeedbackDashboard";
import FeedbackList from "./FeedbackList";
import RoadMap from "./RoadMap";
import Analytics from "./Analytics";
import PopUpModal from "../../modals/PopUpModal";
import {
  reqToCreateFeedBack,
  reqToGetFeedBackStatus,
  reqToGetFeedBackType,
} from "../../redux/slices.js/feedback/feedBackSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

const Index = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = [
    { name: "Dashboard", icon: FeedbackDashbordIcon },
    { name: "Feedback list", icon: FeedbackListIcon },
    { name: "Road Map", icon: RoadMapIcon },
    { name: "Analytics", icon: FeedbackAnalyticsIcon },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0].name);
  const [showModal, setShowModal] = useState(false);
  const [createProgramLoader, setCreateProgramLoader] = useState(false);
  const feedbackType = useSelector((state) => state?.feedbackslice);
  const { type, status } = feedbackType || {};
  const navigate = useNavigate();
  //   state?.feedbackslice

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tabs.find((t) => t.name === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams, tabs]);

  useEffect(() => {
    dispatch(
      reqToGetFeedBackStatus({
        data: {},
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
    dispatch(
      reqToGetFeedBackType({
        data: {},
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch]);

  // Create a ref for the form
  const formRef = useRef();

  // Feedback type options
  const feedbackTypeOptions = [
    { value: "bug", label: "Bug Report" },
    { value: "feature", label: "Feature Request" },
    { value: "improvement", label: "Improvement" },
    { value: "ui-ux", label: "UI/UX Feedback" },
    { value: "other", label: "Other" },
  ];

  // Priority options
  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
  ];

  // Initial values for the form
  const initialValues = {
    feedbackType: "",
    title: "",
    description: "",
    priority: "medium",
  };

  // Validation schema
  const validationSchema = Yup.object({
    feedbackType: Yup.string().required("Feedback type is required"),
    title: Yup.string()
      .required("Title is required")
      .min(5, "Title must be at least 5 characters")
      .max(100, "Title must be less than 100 characters"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(1000, "Description must be less than 1000 characters"),
    priority: Yup.string().required("Priority is required"),
  });

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "1px solid #D1D5DB",
      borderRadius: "6px",
      padding: "2px 8px",
      minHeight: "42px",
      boxShadow: state.isFocused ? "0 0 0 2px #3b82f6" : "none",
      borderColor: state.isFocused ? "#3b82f6" : "#D1D5DB",
      "&:hover": {
        borderColor: state.isFocused ? "#3b82f6" : "#9CA3AF",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#EFF6FF"
        : "white",
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: "#EFF6FF",
      },
    }),
  };

  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    setCreateProgramLoader(true);

    // Prepare payload for API call
    const payload = {
      type: values.feedbackType,
      title: values.title,
      description: values.description,
      priority: values.priority,
    };

    // Dispatch the API call
    dispatch(
      reqToCreateFeedBack({
        data: payload,
        onSuccess: (response) => {
          setCreateProgramLoader(false);
          setSubmitting(false);
          resetForm();
          setShowModal(false);
          // You might want to refresh the feedback list here
          // refreshFeedbackList();
        },
        onFailure: (error) => {
          console.error("Error creating feedback:", error);
          setCreateProgramLoader(false);
          setSubmitting(false);
        },
      })
    );
  };

  // Handle modal confirm button click
  const handleModalConfirm = (handleSubmit) => {
    // Trigger form submission when confirm button is clicked
    if (formRef.current) {
      formRef.current.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  };

  return (
    <>
      <div className="rounded-md border-[1px] border-solid border-[#F3EFEF] space-y-5 md:space-y-8 pb-6 h-full overflow-y-auto">
        <div className="p-3 sm:p-6 bg-[#F5FFBD] space-y-6">
          <div className="mb-4">
            <button
              type="button"
              className="text-[16px] font-normal flex gap-1"
              onClick={() => navigate(-1)}
            >
              {BackArrowIcon} Back
            </button>
          </div>
          <div className="heading flex gap-2 justify-between items-end">
            <div className="heading-title space-y-1">
              <h1 className="text-h2 !font-medium mb-0 text-[#4285F4]">
                Feedback Management
              </h1>
              <p className="text-span text-dark mb-0">
                Collect, prioritize, and respond to user feedback across all
                channels
              </p>
            </div>
          </div>
        </div>
        <div className="px-3 sm:px-6">
          <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
            <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
              <div className="flex items-center gap-2">
                <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
                <div className="">
                  <span className="text-[16px] font-semibold mb-2 block">
                    Multi-Channel Feedback Hub
                  </span>
                  <p className="text-[16px] text-gray-500 text-sm mb-0">
                    Collect feedback from in-app widgets, email, and Slack.
                    Track progress from submission to release with automated
                    notifications and public roadmap visibility.
                  </p>
                </div>
              </div>
              <div className="mt-4 md:mt-0 flex gap-2 flex-col sm:flex-row">
                <button className="whitespace-nowrap btn-light-blue sm:w-[150px] w-full !gap-2">
                  {NewNotificationIcon}New items
                </button>
                <button className="btn-light-gray !text-[#4285F4] whitespace-nowrap btn-light-gray sm:w-[150px] w-full !gap-2 !bg-[#DBE9FF]">
                  {BluePlusIcon}Add Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="px-3 sm:px-6">
          <div className="flex items-center gap-2 md:flex-nowrap flex-wrap">
            <TabsHeader
              tabs={tabs}
              activeTab={activeTab}
              setActiveTab={(tabName) => {
                setActiveTab(tabName);
                setSearchParams({ tab: tabName });
              }}
            />
            {/* <button
              className="mb-3 md:mt-0 -mt-3 w-fit md:mb-6 whitespace-nowrap btn-light-blue sm:w-[150px] !gap-2 flex-none md:m-0 ml-auto"
              onClick={() => {
                setShowModal(true);
              }}
            >
              {plusIcon}Add Feedback
            </button> */}
          </div>
          {activeTab === "Dashboard" && <FeedbackDashboard />}
          {activeTab === "Feedback list" && <FeedbackList />}
          {activeTab === "Road Map" && <RoadMap />}
          {activeTab === "Analytics" && <Analytics />}
        </div>
      </div>

      <PopUpModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleModalConfirm}
        title="Add Feedback"
        confirmText={createProgramLoader ? "Submitting..." : "Submit Feedback"}
        cancelText="Close"
        confirmDisabled={createProgramLoader}
        loading={createProgramLoader}
        size="large"
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, errors, touched, handleSubmit }) => (
            <Form ref={formRef}>
              <div className="space-y-4 p-1">
                {/* Feedback Type */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Type *
                  </label>
                  <Select
                    options={feedbackTypeOptions}
                    value={feedbackTypeOptions.find(
                      (option) => option.value === values.feedbackType
                    )}
                    onChange={(selectedOption) =>
                      setFieldValue("feedbackType", selectedOption?.value || "")
                    }
                    styles={customSelectStyles}
                    isSearchable={false}
                    placeholder="Select a feedback type"
                  />
                  <ErrorMessage
                    name="feedbackType"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Title *
                  </label>
                  <Field
                    type="text"
                    name="title"
                    className="input-field w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief description of your feedback"
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Description *
                  </label>
                  <Field
                    as="textarea"
                    name="description"
                    rows={4}
                    className="input-field w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Please provide detailed information about your feedback"
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Priority *
                  </label>
                  <Select
                    options={priorityOptions}
                    value={priorityOptions.find(
                      (option) => option.value === values.priority
                    )}
                    onChange={(selectedOption) =>
                      setFieldValue(
                        "priority",
                        selectedOption?.value || "medium"
                      )
                    }
                    styles={customSelectStyles}
                    isSearchable={false}
                  />
                  <ErrorMessage
                    name="priority"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </div>

              {/* Hidden submit button for Formik */}
              <button type="submit" className="hidden" />
            </Form>
          )}
        </Formik>
      </PopUpModal>
    </>
  );
};

export default Index;
